import type { ResourceStatusTransition } from "../hooks/useResourceRealtime";

/** Toast severity shown by the GlobalSnackbar. */
export type ToastSeverity = "success" | "error" | "warning" | "info";

export interface StatusToast {
  message: string;
  severity: ToastSeverity;
}

/**
 * Map a resource status transition to a user-facing toast notification.
 *
 * Returns `null` when the transition does not warrant a toast (e.g. the first
 * status report for a resource, or a non-meaningful transition).
 *
 * Meaningful transitions:
 * - provisioning → running:  "Database provisioned successfully" (success)
 * - provisioning → failed:   "Provisioning failed — check details" (error)
 * - any → deprovisioned:     "Resource deprovisioned" (info)
 */
export function statusTransitionToast(
  transition: ResourceStatusTransition,
): StatusToast | null {
  const { previousStatus, newStatus } = transition;

  // The first status report (no previous status) is not a transition.
  if (previousStatus === null) return null;

  if (previousStatus === "provisioning" && newStatus === "running") {
    return {
      message: "Database provisioned successfully",
      severity: "success",
    };
  }

  if (previousStatus === "provisioning" && newStatus === "failed") {
    return {
      message: "Provisioning failed — check details",
      severity: "error",
    };
  }

  if (newStatus === "deprovisioned") {
    return {
      message: "Resource deprovisioned",
      severity: "info",
    };
  }

  return null;
}
